import React, { useId, ReactNode, useMemo, useCallback } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from '@headlessui/react';
import { clsx } from 'clsx';
import { FormField } from './FormField';
import { BaseFormField } from 'types/form/baseFormField';
import { ChevronDown } from 'lucide-react';

export interface DropdownItem<K = number | string> {
  key: K;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  /** Style inline appliqué au label (option + sélection) — ex. aperçu d'une
   * police de caractère rendue dans sa propre fonte. */
  labelStyle?: React.CSSProperties;
  /** Texte discret aligné à droite de l'option (non affiché sur la sélection) —
   * ex. spécimen « AaBbCc 0123 » d'un sélecteur de police. */
  trailingLabel?: string;
  trailingStyle?: React.CSSProperties;
}

interface BaseDropdownProps<K = number | string> {
  items: DropdownItem<K>[];
  selectedItem: DropdownItem<K> | null;
  placeholder?: string;
  disabled?: boolean;
  onSelect?: (selectedItem: DropdownItem<K>) => void;
  variant?: 'default' | 'tiny' | 'medium' | 'no-border';
  className?: string;
}

interface DropdownProps extends BaseFormField, BaseDropdownProps { }

const Dropdown = ({
  label,
  error,
  required,
  name,
  ...props
}: DropdownProps) => (
  <FormField label={label} error={error} required={required} name={name}>
    <DropdownBase {...props} />
  </FormField>
);

const DropdownBase = <K extends string | number>({
  selectedItem,
  items,
  onSelect,
  placeholder,
  disabled,
  variant = 'default',
  className
}: BaseDropdownProps<K>) => {
  const id = useId();

  const getButtonClasses = useCallback(
    (open: boolean) =>
      clsx(
        // Base dropdown-button styles
        'w-full text-base sm:text-sm/6 flex justify-between items-center border px-4 py-4 h-12 rounded-lg bg-white cursor-pointer transition-colors duration-200',
        // Disabled state
        disabled && 'bg-gray-100 cursor-not-allowed',
        // Placeholder styling
        !selectedItem && 'text-gray-400',
        selectedItem && 'text-primary font-medium',
        // Open state
        open ? 'border-primary' : 'border-gray-300',
        // Variants
        {
          'border-none': variant === 'no-border',
          'shadow-none': variant === 'tiny' || variant === 'medium'
        },
        className
      ),
    [disabled, selectedItem, variant, className]
  );

  const menuClasses = useMemo(
    () =>
      clsx(
        // Base dropdown-menu styles — z-[100] must stay above all overlays (see shared/constants/zIndex.ts)
        // Largeur : au moins celle du champ, puis s'élargit pour afficher les options en entier (capée).
        'min-w-[var(--button-width)] w-max max-w-[90vw] bg-white z-[100] overflow-y-auto border border-gray-300 rounded-lg shadow-lg',
        '[--anchor-max-height:60vh]'
      ),
    []
  );

  const getItemClasses = useCallback(
    (focus: boolean, selected: boolean) =>
      clsx(
        // Base dropdown-item styles
        'px-4 py-3 flex justify-start cursor-pointer text-sm transition-colors',
        // Border bottom (not last child equivalent)
        'border-b border-gray-100 last:border-b-0',
        // Focus/selected state
        selected
          ? 'bg-primary text-white font-medium'
          : focus
            ? 'bg-primary/10 text-primary'
            : 'text-gray-700 hover:bg-gray-50'
      ),
    []
  );

  const containerClasses = useMemo(
    () => clsx('w-full relative', disabled && 'opacity-50'),
    [disabled]
  );

  const chevronClasses = useCallback(
    (open: boolean) =>
      clsx(
        'w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0',
        open && 'rotate-180 text-primary'
      ),
    []
  );

  return (
    <Listbox
      as="div"
      onChange={onSelect}
      value={selectedItem}
      className={containerClasses}
    >
      {({ open }) => (
        <>
          <ListboxButton
            id={id}
            className={getButtonClasses(open)}
            disabled={disabled}
          >
            <div className="flex items-center overflow-hidden truncate">
              {selectedItem?.icon && (
                <div className="mr-2 flex-shrink-0">
                  {React.isValidElement(selectedItem.icon) &&
                    React.cloneElement(selectedItem.icon)}
                </div>
              )}
              <span className="truncate" style={selectedItem?.labelStyle}>
                {selectedItem?.label || placeholder || ''}
              </span>
            </div>
            <ChevronDown className={chevronClasses(open)} />
          </ListboxButton>
          <ListboxOptions
            // 'bottom end' : s'aligne sur le bord droit du champ et s'élargit vers la gauche.
            anchor={{ to: 'bottom end', gap: 4, padding: 16 }}
            className={menuClasses}
          >
            {items.map((item) => (
              <ListboxOption
                key={item.key}
                value={item}
                className={({ focus, selected }) =>
                  getItemClasses(focus, selected)
                }
              >
                <span style={item.labelStyle}>{item.label}</span>
                {item.trailingLabel && (
                  <span
                    className="ml-auto pl-8 text-xs opacity-60 whitespace-nowrap self-center"
                    style={item.trailingStyle}
                  >
                    {item.trailingLabel}
                  </span>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </>
      )}
    </Listbox>
  );
};

export { Dropdown, DropdownBase };
